import { addDays, timeOf, todayIso } from '@shkola/api';
import { subjectLook, tones } from '@shkola/design';
import { Link } from '@tanstack/react-router';
import type { TFunction } from 'i18next';
import { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowCounterClockwiseIcon,
  ArrowUpRightIcon,
  CalendarDotsIcon,
  CaretRightIcon,
  CheckCircleIcon,
  ExamIcon,
  type FilledIcon,
  FlagIcon,
  MegaphoneIcon,
  PaperPlaneTiltIcon,
  WarningCircleIcon,
} from '../../design/icons';
import { OnColorProvider, StatusPill, buttonClass, useAge } from '../../design/ui';
import { gradeShort } from '../../lib/labels';
import { AbroadCard } from '../AbroadPage';
import {
  type TaskGroup,
  gradeTone,
  useGradeFeed,
  useNow,
  useSchoolFeed,
  useTaskGroups,
} from './blocks';
import { DateTiles, More, TodoLinkTo, TodoRow, boldGround } from './Cards';
import { FRESH_GROUND, FRESH_RING, FreshDay } from './FreshDay';
import { EVENT_LOOK } from './GlassDay';
import { GlassMood } from './GlassMood';
import { type StudentContext, dayLong, dayMonthShort, metaOf } from './model';

/*
 * «Свіжість» with the home of «Скло» (his words 27.10: «наповнення як у склі, стилістика — свіжість»):
 * the same six blocks of the agreed composition (docs/ux-home-student.md §13) and the same data
 * (blocks.ts) — the greeting with what goes on now and what is next (no picture), «Як ти сьогодні?», the
 * lessons as a week calendar (FreshDay), what to do as cards with the tasks inside, the new grades, the
 * school. Drawn the way of «Свіжість»: the plate and the white panel, white blocks with a hairline,
 * corners of 28 and gaps of 24, the lime-into-lilac ground of the greeting and the days, the bright cards
 * of what to do with their flat pictures and white rows with the one action each.
 */

const BLOCK = `flex min-w-0 flex-col rounded-[28px] bg-white p-4 md:p-6 ${FRESH_RING}`;

/** The title row of a block: the name, a count and ↗ into its section. */
function BlockHead({
  title,
  count,
  to,
  label,
}: {
  title: string;
  count?: number;
  to: '/grades' | '/events' | '/homework';
  label: string;
}) {
  const age = useAge();
  return (
    <header className="mb-4 flex min-h-8 items-center gap-2">
      <h2
        className={`truncate font-display font-semibold tracking-[-0.01em] text-ink ${age === 'junior' ? 'text-[22px] leading-7' : 'text-[18px] leading-6'}`}
      >
        {title}
      </h2>
      {count !== undefined && count > 0 && (
        <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-soft px-2 text-[12px] font-semibold text-muted">
          {count}
        </span>
      )}
      <Link
        to={to}
        aria-label={label}
        title={label}
        className="ml-auto inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-white text-ink ring-1 ring-stroke transition-transform duration-500 ease-spring ring-inset hover:rotate-45"
      >
        <ArrowUpRightIcon size={15} weight="bold" aria-hidden />
      </Link>
    </header>
  );
}

// —— the greeting: what goes on now and what is next ——

/** The white card on the greeting's ground: «Зараз» with what is left, «Далі» with the next lesson. */
function NowCard({ ctx }: { ctx: StudentContext }) {
  const { t } = useTranslation();
  const state = useNow(ctx);
  if (!state.ready) return <div className="h-[208px] rounded-[20px] bg-white/50" aria-busy />;
  const { current, next } = state;
  const tint = tones[current.tone];
  const Icon = current.icon;
  const label = 'text-[13px] leading-5 font-semibold text-muted';
  const nextTone = next ? tones[subjectLook(next.lesson.subject).tone] : undefined;
  return (
    <div className="flex min-w-0 flex-col rounded-[20px] bg-white p-4 shadow-[0_8px_24px_-14px_rgb(30_42_94/0.3)] md:p-5">
      <span className={label}>{t('studentHome.glass.now.now')}</span>
      <div className="mt-1 flex min-w-0 items-center gap-2">
        {Icon && (
          <Icon size={20} weight="fill" aria-hidden color={tint.vivid} className="shrink-0" />
        )}
        <span className="min-w-0 flex-1 truncate font-display text-[20px] leading-7 font-semibold text-ink">
          {current.title}
        </span>
        {current.aside && (
          <StatusPill tone={current.tone} dot={current.live} size="sm">
            {current.aside}
          </StatusPill>
        )}
      </div>
      {current.sub && <p className="truncate text-[13px] text-muted">{current.sub}</p>}
      {current.progress !== undefined && (
        <span
          className="mt-3 block h-1.5 overflow-hidden rounded-full"
          style={{ backgroundColor: tint.soft }}
        >
          <span
            className="block h-full rounded-full"
            style={{
              width: `${Math.round(Math.min(1, Math.max(0, current.progress)) * 100)}%`,
              backgroundColor: tint.vivid,
            }}
          />
        </span>
      )}
      <span className="my-4 block h-px bg-[#E1EAE4]" />
      {next && nextTone ? (
        <>
          <span className={label}>{next.label}</span>
          <div className="mt-2 flex items-center gap-3">
            <span
              className="flex h-12 w-[60px] shrink-0 items-center justify-center rounded-[14px] font-display text-[17px] font-semibold"
              style={{ backgroundColor: nextTone.soft, color: nextTone.vivid }}
            >
              {timeOf(next.lesson.startsAt)}
            </span>
            <span className="min-w-0 flex-1">
              <span className="flex min-w-0 items-center gap-1.5">
                <span className="truncate text-[16px] leading-6 font-semibold text-ink">
                  {next.lesson.subject}
                </span>
                {next.lesson.isTest && (
                  <StatusPill tone="red" size="xs">
                    {t('studentHome.test')}
                  </StatusPill>
                )}
              </span>
              <span className="block truncate text-[13px] text-muted">
                {metaOf(t, next.lesson)}
              </span>
            </span>
          </div>
          {next.extra && <p className="mt-2.5 truncate text-[12.5px] text-muted">{next.extra}</p>}
        </>
      ) : (
        <p className="text-[14px] text-ink-soft">{state.nothing}</p>
      )}
      {state.join && (
        <Link
          to="/lesson/$lessonId"
          params={{ lessonId: state.join.id }}
          className={`mt-4 self-start ${buttonClass('primary', 'sm')}`}
        >
          {t('student.lessons.joinLesson')}
        </Link>
      )}
    </div>
  );
}

function Greeting({ ctx, className = '' }: { ctx: StudentContext; className?: string }) {
  const { t } = useTranslation();
  const age = useAge();
  return (
    <section
      className={`rim relative grid min-w-0 grid-cols-1 items-center gap-5 overflow-hidden rounded-[28px] p-4 md:p-6 lg:grid-cols-[minmax(0,1fr)_minmax(280px,340px)] ${className}`}
      style={{ backgroundImage: FRESH_GROUND }}
    >
      <div className="flex min-w-0 flex-col gap-1.5">
        <p className="flex items-center gap-2 text-[14px] leading-5 font-medium text-ink-soft">
          <CalendarDotsIcon size={16} weight="fill" aria-hidden className="text-tone-blue-vivid" />
          {dayLong(todayIso())}
          {ctx.child.class?.label && <span className="text-muted">· {ctx.child.class.label}</span>}
        </p>
        <h1 className="flex flex-wrap items-baseline gap-x-3">
          <span className="-rotate-3 pr-1 font-hand text-[34px] leading-10 font-bold text-ink-soft md:text-[36px]">
            {t('studentHome.hi')}
          </span>
          <span
            className={`font-display font-semibold tracking-[-0.03em] text-ink ${age === 'junior' ? 'text-[44px] leading-[52px] md:text-[52px] md:leading-[56px]' : 'text-[40px] leading-[48px] md:text-[48px] md:leading-[52px]'}`}
          >
            {ctx.child.firstName}
          </span>
        </h1>
      </div>
      <NowCard ctx={ctx} />
    </section>
  );
}

// —— the latest grades ——

const GRADE_ROW = 58;

function NewGrades({ ctx, className = '' }: { ctx: StudentContext; className?: string }) {
  const { t } = useTranslation();
  const { pending, grades, unread, fresh } = useGradeFeed(ctx);
  // as many rows as the block's height takes (it fills the column under «Треба зробити»)
  const ref = useRef<HTMLDivElement>(null);
  const [room, setRoom] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(([entry]) => entry && setRoom(entry.contentRect.height));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const rows = room > 0 ? Math.min(8, Math.max(2, Math.floor(room / GRADE_ROW))) : 4;
  return (
    <section className={`${BLOCK} ${className}`}>
      <BlockHead
        title={fresh ? t('studentHome.glass.newGrades') : t('studentHome.glass.grades')}
        to="/grades"
        label={t('studentHome.glass.allGrades')}
      />
      <div ref={ref} className="flex min-h-[116px] flex-1 basis-0 flex-col overflow-hidden">
        {pending ? null : grades.length === 0 ? (
          <p className="flex flex-1 items-center justify-center text-[14px] text-muted">
            {t('studentHome.glass.noGrades')}
          </p>
        ) : (
          <ul className="grid grid-cols-1 gap-x-4 gap-y-0.5">
            {grades.slice(0, rows).map((g) => {
              const tone = gradeTone(g);
              return (
                <li key={g.id}>
                  <Link
                    to="/grades"
                    className="flex h-14 items-center gap-3 rounded-[16px] px-1.5 transition-colors hover:bg-soft"
                  >
                    <span
                      className="rim flex size-11 shrink-0 items-center justify-center rounded-[14px] font-display text-[18px] font-semibold"
                      style={{ backgroundImage: boldGround(tone), color: tones[tone].ink }}
                    >
                      {gradeShort(t, g.value, g.special)}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex min-w-0 items-center gap-1.5">
                        <span className="truncate text-[14px] leading-5 font-semibold text-ink">
                          {g.subject}
                        </span>
                        {unread.has(g.id) && (
                          <StatusPill tone="blue" size="xs">
                            {t('studentHome.glass.fresh')}
                          </StatusPill>
                        )}
                      </span>
                      <span className="block truncate text-[12.5px] leading-[18px] text-muted">
                        {g.comment
                          ? `«${g.comment}»`
                          : (g.title ?? g.topic ?? t(`journal.kinds.${g.kind}`))}
                      </span>
                    </span>
                    <span className="shrink-0 text-[12px] text-muted">{dayMonthShort(g.date)}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}

// —— what to do: a bright card per kind, the tasks inside ——

const GROUP_ICON: Record<string, FilledIcon> = {
  overdue: WarningCircleIcon,
  soon: PaperPlaneTiltIcon,
  tests: ExamIcon,
  rework: ArrowCounterClockwiseIcon,
  ack: MegaphoneIcon,
  catchUp: FlagIcon,
  done: CheckCircleIcon,
};

/** What the big number of a card counts. */
function captionOf(t: TFunction, group: TaskGroup): string {
  const count = group.count;
  switch (group.key) {
    case 'overdue':
      return t('studentHome.cards.overdue.caption', { count });
    case 'soon':
      return t('studentHome.cards.soon.week', { count });
    case 'tests':
      return t('studentHome.cards.tests.caption', { count });
    case 'rework':
      return t('studentHome.cards.rework.caption', { count });
    case 'ack':
      return t('studentHome.cards.ack.caption', { count });
    case 'catchUp':
      return t('studentHome.cards.catchUp.caption', { count });
    default:
      return '';
  }
}

/**
 * A kind of task as a card of «Свіжість»: its icon and name, the big number, its picture in the corner,
 * the tasks on white rows (the action in a word where the card is wide enough).
 */
function TaskCard({
  group,
  tall = false,
  className = '',
}: {
  group: TaskGroup;
  /** two rows of the grid high: more tasks inside */
  tall?: boolean;
  className?: string;
}) {
  const { t } = useTranslation();
  const tint = tones[group.tone];
  const Icon = GROUP_ICON[group.key] ?? CheckCircleIcon;
  const Art = group.art;
  const today = todayIso();
  const tomorrow = addDays(today, 1);
  const items = group.rows.flatMap((r) => (r.item ? [r.item] : []));
  const max = tall ? 5 : 2;
  const catchUp = group.key === 'catchUp' ? group.rows[0]?.item : undefined;
  return (
    <OnColorProvider value>
      <div
        className={`rim relative flex min-w-0 flex-col gap-2.5 overflow-hidden rounded-[24px] p-3.5 ${className}`}
        style={{ backgroundImage: boldGround(group.tone) }}
      >
        <Art tone={group.tone} className="absolute -top-1 right-0 w-[88px]" />
        <TodoLinkTo link={group.link} className="relative flex flex-col gap-1 pr-[76px]">
          <span className="flex min-h-7 items-center gap-2">
            <span
              className="flex size-7 shrink-0 items-center justify-center rounded-[9px] bg-white"
              style={{ color: tint.vivid }}
            >
              <Icon size={16} weight="fill" aria-hidden />
            </span>
            <span className="min-w-0 truncate text-[15px] leading-5 font-semibold text-ink">
              {group.title}
            </span>
          </span>
          {group.text ? (
            <span className="flex flex-col gap-0.5 pb-1">
              <span className="font-display text-[22px] leading-7 font-semibold text-ink">
                {group.title}
              </span>
              <span className="text-[13.5px] leading-5" style={{ color: tint.ink }}>
                {group.text}
              </span>
            </span>
          ) : (
            <span className="flex flex-wrap items-baseline gap-x-2">
              <span className="font-display text-[40px] leading-[44px] font-semibold tracking-[-0.03em] text-ink">
                {group.count}
              </span>
              <span className="text-[13px] leading-[18px] font-medium" style={{ color: tint.ink }}>
                {captionOf(t, group)}
              </span>
            </span>
          )}
        </TodoLinkTo>
        {catchUp ? (
          <div className="relative mt-auto flex flex-col gap-1.5">
            <div className="flex flex-wrap gap-1.5">
              {(catchUp.subjects ?? []).slice(0, 4).map((s) => (
                <StatusPill key={s} tone={group.tone} size="sm">
                  {s}
                </StatusPill>
              ))}
            </div>
            <Link
              to="/catch-up"
              className="group flex min-h-12 items-center gap-2 rounded-[16px] bg-white px-3.5 text-[13.5px] font-semibold transition-shadow hover:shadow-[0_2px_12px_rgb(23_23_27/0.08)]"
              style={{ color: tint.vivid }}
            >
              <span className="min-w-0 flex-1 truncate text-ink">
                {t('studentHome.cards.catchUp.hint')}
              </span>
              {t('studentHome.todo.do.catchUp')}
              <CaretRightIcon size={12} weight="bold" aria-hidden />
            </Link>
          </div>
        ) : (
          items.length > 0 && (
            <OnColorProvider value={false}>
              <div className="relative mt-auto flex flex-col gap-1.5">
                <ul className="@container flex flex-col gap-1.5">
                  {items.slice(0, max).map((item) => (
                    <li key={item.key}>
                      <TodoRow item={item} tone={group.tone} today={today} tomorrow={tomorrow} />
                    </li>
                  ))}
                </ul>
                <More count={items.length - max} link={group.link} tone={group.tone} />
              </div>
            </OnColorProvider>
          )
        )}
      </div>
    </OnColorProvider>
  );
}

/** Where the cards stand in a 2×2 grid when there are up to four (the first is the most urgent). */
const SPANS: Record<number, ReadonlyArray<string>> = {
  1: ['sm:col-span-2 sm:row-span-2'],
  2: ['sm:row-span-2', 'sm:row-span-2'],
  3: ['sm:row-span-2', '', ''],
  4: ['', '', '', ''],
};
const TALL: Record<number, number> = { 1: 1, 2: 2, 3: 1 };

function TodoBoard({ ctx, className = '' }: { ctx: StudentContext; className?: string }) {
  const { t } = useTranslation();
  const { groups } = useTaskGroups(ctx);
  const ref = useRef<HTMLDivElement>(null);
  const [edge, setEdge] = useState({ start: true, end: true });
  const many = groups.length > 4;
  const total = groups.filter((g) => g.key !== 'done').reduce((n, g) => n + g.count, 0);
  const measure = () => {
    const el = ref.current;
    if (el)
      setEdge({
        start: el.scrollLeft <= 4,
        end: el.scrollLeft + el.clientWidth >= el.scrollWidth - 4,
      });
  };
  const keys = groups.map((g) => g.key).join(',');
  useLayoutEffect(() => {
    // the cards come one by one as the data loads: start the line from the first one again
    if (ref.current && keys) ref.current.scrollLeft = 0;
    measure();
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [keys]);
  const slide = (dir: number) =>
    ref.current?.scrollBy({ left: dir * (ref.current.clientWidth / 2), behavior: 'smooth' });
  const arrow =
    'absolute top-1/2 z-10 hidden size-10 -translate-y-1/2 items-center justify-center rounded-full bg-white text-ink shadow-[0_6px_18px_-6px_rgb(30_42_94/0.4)] ring-1 ring-[#E1EAE4] ring-inset transition-opacity sm:flex';
  // a phone: one line to swipe; a computer: a 2×2 grid, more than four — two rows that slide
  const layout = many
    ? 'grid-flow-col auto-cols-[86%] grid-rows-1 overflow-x-auto sm:auto-cols-[calc((100%-12px)/2)] sm:grid-rows-2'
    : `grid-flow-col ${groups.length === 1 ? 'auto-cols-[100%]' : 'auto-cols-[86%]'} overflow-x-auto sm:grid-flow-row sm:auto-cols-auto sm:grid-cols-2 sm:grid-rows-2 sm:overflow-visible`;
  return (
    <section className={`${BLOCK} ${className}`}>
      <BlockHead
        title={t('studentHome.glass.todo')}
        count={total}
        to="/homework"
        label={t('studentHome.glass.all')}
      />
      <div className="relative min-w-0">
        <div
          ref={ref}
          onScroll={measure}
          className={`no-scrollbar grid snap-x snap-mandatory gap-3 ${layout}`}
        >
          {groups.map((g, i) => (
            <TaskCard
              key={g.key}
              group={g}
              tall={!many && i < (TALL[groups.length] ?? 0)}
              className={`snap-start ${many ? '' : (SPANS[groups.length]?.[i] ?? '')}`}
            />
          ))}
        </div>
        {many && (
          <>
            <button
              type="button"
              aria-label={t('studentHome.glass.prev')}
              onClick={() => slide(-1)}
              className={`${arrow} -left-3 rotate-180 ${edge.start ? 'pointer-events-none opacity-0' : ''}`}
            >
              <CaretRightIcon size={15} weight="bold" aria-hidden />
            </button>
            <button
              type="button"
              aria-label={t('studentHome.glass.next')}
              onClick={() => slide(1)}
              className={`${arrow} -right-3 ${edge.end ? 'pointer-events-none opacity-0' : ''}`}
            >
              <CaretRightIcon size={15} weight="bold" aria-hidden />
            </button>
          </>
        )}
      </div>
    </section>
  );
}

// —— the school: fresh news and the events of two weeks ——

function SchoolBlock({ ctx, className = '' }: { ctx: StudentContext; className?: string }) {
  const { t } = useTranslation();
  const { news, coming, ago, until } = useSchoolFeed(ctx);
  return (
    <section className={`${BLOCK} ${className}`}>
      <BlockHead
        title={t('studentHome.glass.school.title')}
        to="/events"
        label={t('studentHome.glass.school.all')}
      />
      {news.length > 0 && (
        <ul className="mb-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
          {news.map((a) => (
            <li key={a.id}>
              <Link
                to="/announcements"
                className={`flex items-center gap-3 rounded-[20px] bg-white px-3 py-2.5 transition-shadow hover:shadow-[0_2px_12px_rgb(23_23_27/0.08)] ${FRESH_RING}`}
              >
                <span
                  className="rim flex size-9 shrink-0 items-center justify-center rounded-[12px]"
                  style={{ backgroundImage: boldGround('pink'), color: tones.pink.vivid }}
                >
                  <MegaphoneIcon size={17} weight="fill" aria-hidden />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[14px] leading-5 font-semibold text-ink">
                    {a.title}
                  </span>
                  <span className="block truncate text-[12.5px] leading-[18px] text-muted">
                    {[a.author, ago(a.publishedAt)].filter(Boolean).join(' · ')}
                  </span>
                </span>
                <StatusPill tone="blue" dot size="xs">
                  {t('studentHome.glass.school.news')}
                </StatusPill>
              </Link>
            </li>
          ))}
        </ul>
      )}
      {coming.length === 0 ? (
        <p className="flex flex-1 items-center justify-center py-6 text-[14px] text-muted">
          {t('studentHome.glass.school.empty')}
        </p>
      ) : (
        <div className="grid flex-1 grid-cols-2 gap-3 xl:grid-cols-4">
          {coming.map((e) => {
            const look = EVENT_LOOK[e.kind];
            const tint = tones[look.tone];
            const Icon = look.icon;
            return (
              <Link
                key={e.id}
                to="/events"
                className="rim relative flex min-h-[148px] min-w-0 flex-col overflow-hidden rounded-[24px] p-3.5 transition-transform duration-300 hover:-translate-y-0.5 sm:p-4"
                style={{ backgroundImage: boldGround(look.tone) }}
              >
                {/* on a narrow card the «через N днів» goes under the date */}
                <span className="flex flex-wrap items-start justify-between gap-2">
                  <DateTiles date={e.startsOn} tone={look.tone} />
                  <OnColorProvider value>
                    <StatusPill tone={look.tone} size="xs" className="max-w-full truncate">
                      {until(e.startsOn, e.endsOn)}
                    </StatusPill>
                  </OnColorProvider>
                </span>
                <span className="mt-auto line-clamp-2 pt-3 text-[15px] leading-5 font-semibold text-ink">
                  {e.title}
                </span>
                <span
                  className="mt-1 flex min-w-0 items-center gap-1 text-[12.5px] leading-[18px] font-medium"
                  style={{ color: tint.ink }}
                >
                  <Icon
                    size={13}
                    weight="fill"
                    aria-hidden
                    color={tint.vivid}
                    className="shrink-0"
                  />
                  <span className="truncate">
                    {[e.atTime, e.place].filter(Boolean).join(' · ') || dayMonthShort(e.startsOn)}
                  </span>
                </span>
              </Link>
            );
          })}
        </div>
      )}
    </section>
  );
}

// —— the page ——

export function FreshHome({ ctx }: { ctx: StudentContext }) {
  return (
    <div className="grid grid-cols-1 gap-4 md:gap-6 xl:grid-cols-12">
      <div className="min-w-0 empty:hidden xl:col-span-12">
        <AbroadCard studentId={ctx.child.memberId} self />
      </div>
      <Greeting ctx={ctx} className="xl:col-span-8" />
      <GlassMood
        ctx={ctx}
        frame={`rounded-[28px] bg-white p-4 md:p-6 ${FRESH_RING}`}
        className="max-xl:order-6 xl:col-span-4"
      />
      <FreshDay ctx={ctx} className="xl:col-span-5" />
      {/* the column beside the lessons: what to do, the grades fill the rest of its height */}
      <div className="flex min-w-0 flex-col gap-4 max-xl:contents md:gap-6 xl:col-span-7">
        <TodoBoard ctx={ctx} />
        <NewGrades ctx={ctx} className="max-xl:order-4 xl:flex-1" />
      </div>
      <SchoolBlock ctx={ctx} className="max-xl:order-5 xl:col-span-12" />
    </div>
  );
}
